import { toDate } from '@/utils/function'
import {
    Contract,
    ContractServiceSLA,
    ServiceConfigOption,
    ServiceTimes,
} from '../../types'
import { PriceRange } from '@/modules/Service/types'

export const buildServiceTimes = (service_times?: ServiceTimes) => {
    return {
        ...service_times,
        statement_enddate: toDate(service_times?.statement_enddate),
        statement_startdate: toDate(service_times?.statement_startdate),
        continue_enddate: toDate(service_times?.continue_enddate),
        continue_startdate: toDate(service_times?.continue_startdate),
        first_enddate: toDate(service_times?.first_enddate),
        first_startdate: toDate(service_times?.first_startdate),
        current_enddate: toDate(service_times?.first_enddate),
        current_startdate: toDate(service_times?.first_startdate),
    }
}

// Normalize commitment dates to "YYYY-MM-DD". The form holds them as Dayjs objects
// which otherwise serialize to ISO timestamps the backend (pgtype.Date) rejects.
export const buildServiceConfigoptions = (
    service_configoptions?: ServiceConfigOption[]
) => {
    if (!service_configoptions?.length) return service_configoptions
    return service_configoptions.map((option) => ({
        ...option,
        // Coerce numeric fields: empty inputs hold "" which the backend's
        // float64 commitment fields reject during bind (→ opaque 500).
        commitment_value: Number(option.commitment_value) || 0,
        commitment_price_per_unit:
            Number(option.commitment_price_per_unit) || 0,
        commitment_startdate: toDate(option.commitment_startdate),
        commitment_enddate: toDate(option.commitment_enddate),
    }))
}

export const buildServiceSLA = (
    service_sla?: ContractServiceSLA,
    contract_effective_date?: string | null,
    contract_expiry_date?: string | null
): ContractServiceSLA | undefined => {
    if (!service_sla) return undefined

    return {
        ...service_sla,
        // Use contract dates as effective dates if not specified
        effective_from:
            toDate(service_sla.effective_from) || contract_effective_date,
        effective_to: toDate(service_sla.effective_to) || contract_expiry_date,
    }
}

const buildOtherStandardMetadata = (
    service_domain_metadata?: Record<string, any>
) => {
    const schedule =
        service_domain_metadata?.other_standard_payment_schedule ||
        service_domain_metadata?.payment_schedule
    if (!schedule?.ranges?.length) return service_domain_metadata

    return {
        ...service_domain_metadata,
        other_standard_payment_schedule: {
            ...schedule,
            ranges: schedule.ranges.map((range: Record<string, any>) => ({
                ...range,
                start_date: toDate(range.start_date),
                end_date: toDate(range.end_date),
                price: Number(range.price || 0),
            })),
        },
    }
}

const isOtherStandardProduct = (
    product: Partial<Contract['order']['products'][number]>
) => {
    return (
        product.template_name === 'Other.Standard' ||
        product.product_group_code?.toUpperCase?.() === 'OTHER'
    )
}

const reformatContractPayload = (values: Contract) => {
    const contract_effective_date = toDate(values.contract_effective_date)
    const contract_expiry_date = toDate(values.contract_expiry_date)

    const payload: Contract = {
        ...values,
        contract_effective_date,
        contract_expiry_date,
        // Include service_sla only if sla_enable is true
        service_sla: values.sla_enable
            ? buildServiceSLA(
                  values.service_sla,
                  contract_effective_date,
                  contract_expiry_date
              )
            : undefined,
        order: {
            ...values.order,
            // @ts-ignore
            products: values?.order.products.map((product) => {
                const shouldUseOtherStandard = isOtherStandardProduct(product)

                return {
                    ...product,
                    template_name: shouldUseOtherStandard
                        ? 'Other.Standard'
                        : product.template_name,
                    service_configoptions: shouldUseOtherStandard
                        ? []
                        : (product.service_configoptions || []).map(
                              (option) => ({
                                  ...option,
                                  capacity_value: option.capacity_value
                                      ? parseFloat(
                                            option.capacity_value.toString()
                                        )
                                      : undefined,
                                  commitment_startdate: toDate(
                                      option.commitment_startdate
                                  ),
                                  commitment_enddate: toDate(
                                      option.commitment_enddate
                                  ),
                                  prices: (option.prices || [])?.map(
                                      (price?: PriceRange) => ({
                                          ...price,
                                          price_per_unit:
                                              parseInt(
                                                  price?.price_per_unit?.toString() ||
                                                      '0'
                                              ) || 0,
                                          price_start:
                                              parseInt(
                                                  price?.price_start?.toString() ||
                                                      '0'
                                              ) || 0,
                                          price_end:
                                              parseInt(
                                                  price?.price_end?.toString() ||
                                                      '0'
                                              ) || 0,
                                      })
                                  ),
                              })
                          ),
                    service_times: buildServiceTimes(product?.service_times),
                    service_domain_metadata: shouldUseOtherStandard
                        ? buildOtherStandardMetadata(
                              product.service_domain_metadata
                          )
                        : product.service_domain_metadata,
                }
            }),
        },
    }

    return payload
}

export default reformatContractPayload
